/**
 * Brand loader: the single description of the animated MyEquip mark.
 *
 * Two consumers must stay identical, otherwise the screen jumps when React
 * takes over from the static page:
 *  - `index.html`, where the start screen is painted before any script runs
 *    (injected at build time by `vite.config.ts` from this module);
 *  - `components/brand-loader.tsx`, used by every loading state of the app.
 *
 * This module is pure (no DOM, no React) so the build configuration can import it.
 */

/**
 * Full start animation, in milliseconds, on a cold start of the app.
 * THE value to change to make the start screen longer or shorter: every step of
 * the animation is expressed as a share of this duration.
 */
export const STARTUP_ANIMATION_MS = 2500;
/** Start animation when the app reloads within the same session (update, refresh). */
export const STARTUP_QUICK_MS = 500;
/** An in-page loader stays invisible this long: a fast answer never flashes it. */
export const LOADER_REVEAL_DELAY_MS = 180;
/** Fade of the start screen once the app is ready. */
export const STARTUP_FADE_MS = 220;
/** Session marker: the long animation has already been played in this tab or app launch. */
export const STARTUP_SEEN_KEY = 'fa.launch.seen';
/** Below this width the phone composition is used (same threshold as the shell). */
export const DESKTOP_MIN_WIDTH = 900;

export type BrandLoaderVariant = 'startup-mobile' | 'startup-desktop' | 'page' | 'section';

/**
 * Supplied vector symbol (`public/symbol.svg`, Index Beacon v2), split into its
 * three parts; `scripts/build-brand-vectors.mjs` draws the same geometry.
 */
export const MARK_VIEWBOX = '0 0 96 96';
export const MARK_UPPER = 'M12 12H56V26H26V56H12Z';
export const MARK_LOWER = 'M84 84H40V70H70V40H84Z';
export const MARK_DOT = { x: 66, y: 12, size: 18 } as const;

export function brandLoaderClass(variant: BrandLoaderVariant): string {
  return `brand-loader brand-loader--${variant}`;
}

/** Inner markup of a loader; the React component renders exactly this tree. */
export function brandLoaderArt(): string {
  const dot = `x="${MARK_DOT.x}" y="${MARK_DOT.y}" width="${MARK_DOT.size}" height="${MARK_DOT.size}"`;
  return (
    '<span class="brand-loader__art" aria-hidden="true">'
    + `<svg class="brand-loader__mark" viewBox="${MARK_VIEWBOX}" focusable="false">`
    + `<path class="brand-loader__upper" d="${MARK_UPPER}"></path>`
    + `<path class="brand-loader__lower" d="${MARK_LOWER}"></path>`
    + `<rect class="brand-loader__halo" ${dot}></rect>`
    + `<rect class="brand-loader__dot" ${dot}></rect>`
    + '</svg>'
    + '<span class="brand-loader__word"></span>'
    + '</span>'
  );
}

/**
 * Published path of the start screen script. `vite.config.ts` serves and emits
 * it from `startupScreenScript()`: a file, not an inline script, because the
 * Content-Security-Policy only allows scripts served by the application.
 */
export const STARTUP_SCRIPT_PATH = '/startup-screen.js';

/** Chooses the composition (phone or wide screen) and the short replay. */
export function startupScreenScript(): string {
  return (
    '(function(){var l=document.getElementById("launch");if(!l)return;'
    + `if(window.matchMedia("(min-width: ${DESKTOP_MIN_WIDTH}px)").matches)`
    + 'l.className="brand-loader brand-loader--startup-desktop";'
    + `try{if(sessionStorage.getItem("${STARTUP_SEEN_KEY}"))l.className+=" brand-loader--quick";}catch(e){}`
    // Safety net: a start screen never outlives a failed start.
    + 'setTimeout(function(){var n=document.getElementById("launch");if(n)n.remove();},10000);'
    + '})();\n'
  );
}

/**
 * Start screen of `index.html`. Its script is synchronous and placed right
 * after the element, so the first painted frame is already the right one.
 */
export function startupScreenHtml(): string {
  return (
    `<div id="launch" class="brand-loader brand-loader--startup-mobile" aria-hidden="true">${brandLoaderArt()}</div>`
    + `<script src="${STARTUP_SCRIPT_PATH}"></script>`
  );
}

/** Durations handed to the stylesheet, so CSS and scripts share the constants above. */
export function brandLoaderCssVariables(): string {
  return (
    `.brand-loader{--bl-startup:${STARTUP_ANIMATION_MS}ms;--bl-reveal-delay:${LOADER_REVEAL_DELAY_MS}ms;`
    + `--bl-fade:${STARTUP_FADE_MS}ms}`
    + `.brand-loader--quick{--bl-startup:${STARTUP_QUICK_MS}ms}`
  );
}
